import React, { useState } from 'react';
import PropertyCard from './PropertyCard';
import './OffersGrouped.css';

function OffersGrouped({ 
  offers, 
  listings, 
  onAccept, 
  onCounter, 
  onReject, 
  onAIReview,
  processingOffer,
  collapsedListings = {},
  setCollapsedListings
}) {
  // Use local state as fallback if props not provided
  const [localCollapsed, setLocalCollapsed] = useState({});
  
  const collapsed = setCollapsedListings ? collapsedListings : localCollapsed;
  const setCollapsed = setCollapsedListings || setLocalCollapsed;

  // Group offers by listing_id
  const groupedOffers = offers.reduce((acc, offer) => {
    const listingId = offer.listing_id;
    if (!acc[listingId]) {
      acc[listingId] = [];
    }
    acc[listingId].push(offer);
    return acc;
  }, {});

  const toggleListing = (listingId) => {
    setCollapsed(prev => ({
      ...prev,
      [listingId]: !prev[listingId]
    }));
  };

  const formatCurrency = (amount) => {
    if (!amount) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getListingInfo = (listingId) => {
    const listing = listings.find(l => l.id === parseInt(listingId)) || {};
    const listingOffers = groupedOffers[listingId] || [];
    const firstOffer = listingOffers[0] || {};
    
    return {
      listing,
      address: firstOffer.listing_address || listing.address || 'Unknown Address',
      city: firstOffer.listing_city || listing.city || '',
      state: firstOffer.listing_state || listing.state || '',
      offerCount: listingOffers.length,
      highestOffer: Math.max(...listingOffers.map(o => o.offer_amount || 0)),
      pendingCount: listingOffers.filter(o => o.status === 'pending').length,
      images: listing.images
    };
  };

  const listingIds = Object.keys(groupedOffers);

  // If only one listing, don't show grouping - just show offers directly
  if (listingIds.length <= 1) {
    return (
      <div className="offers-list">
        {offers.map(offer => {
          const listing = listings.find(l => l.id === offer.listing_id) || {};
          return (
            <PropertyCard
              key={offer.id}
              stage="offers"
              property={{
                address: offer.listing_address,
                city: offer.listing_city,
                state: offer.listing_state,
                images: listing.images,
                bedrooms: listing.bedrooms,
                bathrooms: listing.bathrooms,
                square_feet: listing.square_feet,
                property_type: listing.property_type
              }}
              status={offer.status}
              offer={{
                offer_amount: offer.offer_amount,
                buyer_name: offer.buyer_name || `${offer.buyer_first_name || ''} ${offer.buyer_last_name || ''}`.trim(),
                financing_type: offer.financing_type,
                earnest_money: offer.earnest_money,
                closing_date: offer.closing_date,
                counter_amount: offer.counter_amount,
                is_external: offer.is_external_offer
              }}
              actions={offer.status === 'pending' ? [
                { label: 'Accept', onClick: () => onAccept(offer.id), disabled: processingOffer },
                { label: 'Counter', onClick: () => onCounter(offer), disabled: processingOffer },
                { label: 'Reject', onClick: () => onReject(offer.id), disabled: processingOffer },
                { label: 'AI Review', onClick: () => onAIReview(listing), variant: 'purple' }
              ] : []}
            />
          );
        })}
      </div>
    );
  }

  return (
    <div className="offers-grouped">
      {listingIds.map(listingId => {
        const info = getListingInfo(listingId);
        const isCollapsed = collapsed[listingId];
        const listingOffers = groupedOffers[listingId];

        return (
          <div key={listingId} className={`listing-group ${isCollapsed ? 'collapsed' : 'expanded'}`}>
            {/* Collapsible Header */}
            <div className="listing-group-header" onClick={() => toggleListing(listingId)}>
              {/* Thumbnail */}
              <div className="listing-group-thumbnail">
                {info.images && info.images.length > 0 ? (
                  <img src={info.images[0].image_url} alt={info.address} />
                ) : (
                  <div className="thumbnail-placeholder">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <rect x="3" y="3" width="18" height="18" rx="2"/>
                      <circle cx="8.5" cy="8.5" r="1.5"/>
                      <polyline points="21 15 16 10 5 21"/>
                    </svg>
                  </div>
                )}
              </div>

              {/* Address */}
              <div className="listing-group-address">
                <h3>{info.address}</h3>
                <p>{info.city}{info.city && info.state ? ', ' : ''}{info.state}</p>
              </div>

              {/* Stats */}
              <div className="listing-group-stats">
                <div className="stat">
                  <span className="stat-value">{info.offerCount}</span>
                  <span className="stat-label">Offer{info.offerCount !== 1 ? 's' : ''}</span>
                </div>
                {info.pendingCount > 0 && (
                  <div className="stat pending">
                    <span className="stat-value">{info.pendingCount}</span>
                    <span className="stat-label">Pending</span>
                  </div>
                )}
                <div className="stat">
                  <span className="stat-value">{formatCurrency(info.highestOffer)}</span>
                  <span className="stat-label">Highest</span>
                </div>
              </div>

              {/* Arrow */}
              <div className="listing-group-arrow">
                <svg 
                  width="20" 
                  height="20" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2"
                  className={isCollapsed ? '' : 'rotated'}
                >
                  <polyline points="6 9 12 15 18 9"/>
                </svg>
              </div>
            </div>

            {/* Expanded Offers */}
            {!isCollapsed && (
              <div className="listing-group-offers">
                {listingOffers.map(offer => (
                  <PropertyCard
                    key={offer.id}
                    stage="offers"
                    property={{
                      address: offer.listing_address,
                      city: offer.listing_city,
                      state: offer.listing_state,
                      images: info.listing.images,
                      bedrooms: info.listing.bedrooms,
                      bathrooms: info.listing.bathrooms,
                      square_feet: info.listing.square_feet,
                      property_type: info.listing.property_type
                    }}
                    status={offer.status}
                    offer={{
                      offer_amount: offer.offer_amount,
                      buyer_name: offer.buyer_name || `${offer.buyer_first_name || ''} ${offer.buyer_last_name || ''}`.trim(),
                      financing_type: offer.financing_type,
                      earnest_money: offer.earnest_money,
                      closing_date: offer.closing_date,
                      counter_amount: offer.counter_amount,
                      is_external: offer.is_external_offer
                    }}
                    actions={offer.status === 'pending' ? [
                      { label: 'Accept', onClick: () => onAccept(offer.id), disabled: processingOffer },
                      { label: 'Counter', onClick: () => onCounter(offer), disabled: processingOffer },
                      { label: 'Reject', onClick: () => onReject(offer.id), disabled: processingOffer },
                      { label: 'AI Review', onClick: () => onAIReview(info.listing), variant: 'purple' }
                    ] : []}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default OffersGrouped;
